import React, { useState } from 'react'
import {Search, Filter, Rss} from 'lucide-react'
import BlogCard from '@/components/BlogCard'
import { blogPosts, categories, getPostsByCategory } from '@/data/blogPosts'

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All Posts')
  const [searchQuery, setSearchQuery] = useState('')

  const filteredPosts = getPostsByCategory(selectedCategory).filter(post =>
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-2xl p-8 md:p-12 mb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Roofing Insights & Expert Guides
        </h1>
        <p className="text-xl text-blue-100 max-w-3xl mb-4">
          Stay informed with our comprehensive guides on roofing costs, materials, maintenance, and industry trends. Expert advice to help you make confident roofing decisions.
        </p>
        <a
          href="/rss.xml"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium text-sm"
        >
          <Rss className="w-5 h-5" />
          Subscribe to RSS Feed
        </a>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search articles by keyword, topic, or tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white cursor-pointer min-w-[200px]"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mt-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-6">
        <p className="text-gray-600">
          Showing <span className="font-semibold text-gray-900">{filteredPosts.length}</span> article{filteredPosts.length !== 1 ? 's' : ''}
          {selectedCategory !== 'All Posts' && (
            <span> in <span className="font-semibold text-blue-600">{selectedCategory}</span></span>
          )}
          {searchQuery && (
            <span> matching "<span className="font-semibold text-blue-600">{searchQuery}</span>"</span>
          )}
        </p>
      </div>

      {/* Blog Posts Grid */}
      {filteredPosts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredPosts.map((post) => (
            <BlogCard key={post.id} post={post} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="text-gray-400 mb-4">
            <Search size={64} className="mx-auto" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">No articles found</h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search or filter criteria
          </p>
          <button
            onClick={() => {
              setSearchQuery('')
              setSelectedCategory('All Posts')
            }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* SEO Content Section */}
      <div className="bg-gray-50 rounded-xl p-8 mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Your Trusted Source for Roofing Information
        </h2>
        <div className="prose prose-lg max-w-none text-gray-700">
          <p className="mb-4">
            Welcome to the RoofingCostsCalculator.com blog, your comprehensive resource for everything related to roofing costs, materials, installation, and maintenance. Our expert-written guides help homeowners and property managers make informed decisions about their roofing projects.
          </p>
          <p className="mb-4">
            Whether you're planning a complete roof replacement, comparing metal vs asphalt shingles, or exploring eco-friendly options like solar tiles or green roofs, our detailed cost guides provide accurate, up-to-date pricing information for your region.
          </p>
          <p>
            Use our free roofing calculators alongside these articles to get instant, customized estimates for your specific project. From flat commercial roofs to residential pitched roofs, we cover all roofing types, materials, and budgets.
          </p>
        </div>
      </div>
    </div>
  )
}
